export interface SportSource {
  title: string;
  url: string;
  source_type?: string;
  is_primary?: boolean;
}

export interface RelatedSport {
  slug: string;
  common_name: string;
  relation_type: string;
}

export interface SportLeagueSummary {
  slug: string;
  name: string;
  short_name: string;
  free_capabilities: string[];
}

export interface SportPresentation {
  show_card: boolean;
  show_ai_overview: boolean;
}

export interface SportRecord {
  slug: string;
  common_name: string;
  sport_type: string;
  category: string;
  subcategory: string;
  summary: string;
  governing_body: string;
  team_size: string;
  equipment: string[];
  aliases: string[];
  sources: SportSource[];
  related_sports: RelatedSport[];
  leagues: SportLeagueSummary[];
  presentation: SportPresentation;
  is_verified: boolean;
  active: boolean;
  registry_version: string;
}

export interface SportsTeamSummary {
  name: string;
  abbreviation: string;
  icon_url: string;
  official_url?: string;
  score: number | null;
}

export interface SportsScheduleGame {
  id: string;
  display_type?: "game" | "event" | string;
  starts_at: string | null;
  status: string;
  status_state: string;
  stage: string;
  week: number | null;
  venue: string;
  home_team: SportsTeamSummary;
  away_team: SportsTeamSummary;
}

export interface SportsLeagueSchedule {
  league_slug: string;
  league_name: string;
  league_short_name: string;
  sport_name: string;
  season: number | null;
  season_display_name: string;
  cache: {
    last_synced_at: string | null;
    last_sync_attempt_at: string | null;
    last_sync_error: string | null;
    sync_status: string | null;
  };
  games: SportsScheduleGame[];
}

export interface SportsLeagueHubResource {
  label: string;
  kind: string;
  requires: string[];
}

export interface SportsLeagueHub {
  enabled: boolean;
  default_resource: string;
  tabs: string[];
  free_resources: Record<string, SportsLeagueHubResource>;
}

export interface SportsProviderResourceResponse {
  league: string;
  resource: string;
  refreshed?: boolean;
  payload?: {
    data?: unknown[];
    meta?: Record<string, unknown>;
    pages_fetched?: number;
    truncated?: boolean;
  };
  requires?: string[];
  error?: string;
}

export interface SportsLeagueRecord {
  slug: string;
  name: string;
  short_name: string;
  sport: {
    slug: string;
    common_name: string;
  };
  provider: string;
  provider_sport_key: string;
  free_capabilities: string[];
  current_season: number | null;
  schedule_enabled: boolean;
  schedule_access: "free" | "subscription_or_trial" | "subscription_required" | "disabled" | string;
  schedule_available_free: boolean;
  presentation: SportPresentation & { show_schedule_card?: boolean };
  hub?: SportsLeagueHub;
  cache: {
    last_synced_at: string | null;
    last_sync_error: string | null;
  };
  schedule: SportsLeagueSchedule | null;
}

export interface SportResolveResponse {
  matched: boolean;
  query: string;
  resolved_type: "sport" | "league" | null;
  sport: SportRecord | null;
  league: SportsLeagueRecord | null;
  error?: string;
}

const SPORTS_RESOLVE_API_URL =
  "https://api.ayesearch.com/api/sports/resolve/";

export async function resolveSport(
  query: string,
): Promise<SportResolveResponse> {
  const url = new URL(SPORTS_RESOLVE_API_URL);
  url.searchParams.set("q", query);

  const response = await fetch(url.toString(), {
    method: "GET",
    credentials: "include",
    headers: {
      Accept: "application/json",
    },
  });

  if (!response.ok) {
    throw new Error(`Sports resolver returned HTTP ${response.status}`);
  }

  return (await response.json()) as SportResolveResponse;
}


const SPORTS_PROVIDER_RESOURCE_API_URL =
  "https://api.ayesearch.com/api/sports/provider-resource/";

export async function fetchLeagueResource(
  league: string,
  resource: string,
  params: Record<string, string | number> = {},
): Promise<SportsProviderResourceResponse> {
  const url = new URL(SPORTS_PROVIDER_RESOURCE_API_URL);
  url.searchParams.set("league", league);
  url.searchParams.set("resource", resource);
  for (const [key, value] of Object.entries(params)) {
    if (value !== "" && value !== null && value !== undefined) {
      url.searchParams.set(key, String(value));
    }
  }

  const response = await fetch(url.toString(), {
    method: "GET",
    credentials: "include",
    headers: { Accept: "application/json" },
  });

  const payload = (await response.json()) as SportsProviderResourceResponse;
  if (!response.ok) {
    throw new Error(payload.error || `Sports resource returned HTTP ${response.status}`);
  }
  return payload;
}
